import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, ShieldCheck, Users, FileText, Settings, Wrench, Calendar,
    CalendarOff, Bell, CalendarClock,
    BarChart3, Lock, ArrowUpRight, LayoutGrid, Database,
} from "lucide-react";
import { HardHat } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import DataManagementTab from "./DataManagementTab";

/**
 * `/operations/administration` — permission-gated hub linking out to
 * the existing admin consoles. Each card only renders when the acting
 * user carries the matching permission.
 *
 * Two tabs:
 *  • Overview        — the admin cards (Users, Templates, System, …)
 *  • Data Management — Wipe All Demo Data control (requires
 *                      `manage_system_settings`)
 */
const CARDS = [
    { perm: "manage_users",           to: "/settings/security",       icon: Users,      label: "Users & security",       desc: "Manage user accounts, permissions, sessions and the security audit trail." },
    // DEV-023 · Notifications moved out of the main sidebar and into
    // Administration. Preserves the existing `/operations/notifications`
    // Notification Centre and its access model — every Operations user
    // who could reach the standalone sidebar entry can still open it
    // from here (route stays role-gated to `operations`; no permission
    // is required, so this admin card has `perm: null` and is always
    // visible when the Administration hub itself is accessible).
    { perm: null,                     to: "/operations/notifications", icon: Bell,       label: "Notifications",         desc: "Central inbox of programme notifications — projects, tasks, appointments and workflow events." },
    { perm: "manage_templates",       to: null,                       icon: FileText,   label: "Templates",              desc: "Reusable task, appointment and document templates for the operations team." },
    // DEV-023B (final MVP) · System Settings is now its own hub
    // (`/operations/administration/system-settings`) which groups
    // the existing Data Management and Run Identity Audit surfaces.
    // The previous card mistakenly pointed at `/settings/notifications`
    // — that was Notifications preferences masquerading as System
    // Settings. The overview card is visible when the user has EITHER
    // manage_system_settings OR manage_users (matches the union of
    // the two child permissions); unauthorised children stay hidden
    // inside the hub.
    { perm: null, predicate: (has) => has("manage_system_settings") || has("manage_users"),
      to: "/operations/administration/system-settings", icon: Settings, label: "System Settings",
      desc: "Data Management and Run Identity Audit — everything that maintains programme data integrity." },
    { perm: "manage_engineers",       to: "/operations/administration/field-team",      icon: HardHat, label: "Field Team Management",  desc: "Add, edit, archive and classify internal people, external contractors and external organisations. Sole administration surface for the field team." },
    // DEV-024 · Scheduling sub-hub. Groups Resource working hours
    // (deep-linked to Field Team Management) and Blackout periods
    // under one Administration entry. The standalone Blackout
    // periods overview card is retired — its URL is preserved and
    // accessible through Scheduling. The misleading "Calendar
    // policies" tile that pointed at the ordinary Calendar is
    // removed (Calendar Policies is explicitly future scope).
    // Uses a permission predicate on the card so it stays visible
    // when the user carries EITHER child permission — unauthorised
    // children are still hidden inside the sub-hub.
    { perm: null, predicate: (has) => has("manage_calendar") || has("manage_engineers"),
      to: "/operations/administration/scheduling", icon: CalendarClock, label: "Scheduling", desc: "Resource working hours and blackout periods — everything that shapes day-to-day scheduling." },
    { perm: "view_reports",           to: "/operations/reports",      icon: BarChart3,  label: "Reports",                desc: "Fleet-wide KPIs, engineer productivity and compliance dashboards." },
];

export default function OperationsAdministration() {
    const { hasPermission, user } = useAuth();
    // DEV-023 · Cards with `perm: null` are always visible; DEV-024
    // adds an optional `predicate(hasPermission)` for cards whose
    // access maps to multiple permissions (Scheduling = manage_calendar
    // OR manage_engineers).
    const visible = CARDS.filter((c) => {
        if (c.predicate) return c.predicate(hasPermission);
        if (!c.perm) return true;
        return hasPermission(c.perm);
    });
    const [tab, setTab] = useState("overview");
    // DEV-023B (final MVP) · Data Management moved into the new
    // System Settings hub (`/operations/administration/system-settings`),
    // so the old in-page Data Management tab is retired here.
    const identity = portalIdentity(user?.role);

    return (
        <OperationsShell>
            <div className="mx-auto max-w-6xl px-4 md:px-8 pt-6 md:pt-10 pb-16" data-testid="ops-administration-page">
                <BackButton fallbackHref="/operations" testid="ops-administration-back" className="mb-4" />

                {/* DEV-022 · Administration premium hero — Admin no
                    longer looks like a config back-room. */}
                <PortalHero
                    testid="ops-admin-hero"
                    eyebrow={`${identity.eyebrow} · ADMINISTRATION`}
                    title="Administration"
                    sentence={
                        <>
                            Only the administration tools available to you are shown here.
                            {visible.length > 0 && (
                                <> <strong className="text-white font-semibold">{visible.length}</strong> {visible.length === 1 ? "console" : "consoles"} available.</>
                            )}
                        </>
                    }
                    ring={{ value: visible.length, label: visible.length === 1 ? "console" : "consoles" }}
                    tag="Permission-gated"
                />

                <div className="mt-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ops-admin-cards">
                        {visible.length === 0 ? (
                            <div className="md:col-span-2 rounded-xl border border-[#E5E7EB] bg-white p-8 text-center" data-testid="ops-admin-no-perms">
                                <Lock size={24} strokeWidth={1.5} className="mx-auto text-[#4B5565] mb-2" />
                                <div className="text-sm font-medium text-[#131B2F]">No administrative permissions assigned.</div>
                                <div className="text-xs text-[#4B5565] mt-1">
                                    Ask a Security Administrator to grant you one of: Manage Users, Templates, System Settings, Engineers, Calendar, Reports.
                                </div>
                            </div>
                        ) : visible.map((c) => (
                            <AdminCard key={c.to || c.label} {...c} />
                        ))}
                    </div>
                </div>
            </div>
        </OperationsShell>
    );
}

function TabBtn(_) { return null; }

function AdminCard({ to, icon: Icon, label, desc, perm }) {
    // DEV-023 / DEV-024 / DEV-023B · Cards without a single permission
    // (Notifications, Scheduling, System Settings) still need a
    // stable, unique testid. Historic permission-based testids are
    // preserved for backwards-compatible tests.
    const SLUG_BY_LABEL = {
        Notifications:    "notifications",
        Scheduling:       "scheduling",
        "System Settings":"system-settings",
    };
    const testid = perm
        ? `ops-admin-card-${perm}`
        : `ops-admin-card-${SLUG_BY_LABEL[label] || "custom"}`;
    const inner = (
        <>
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-[#EEF2FF] text-[#1E3A8A] flex items-center justify-center shrink-0">
                    <Icon size={18} strokeWidth={1.75} />
                </span>
                <div className="text-sm font-semibold text-[#131B2F] flex-1 min-w-0">{label}</div>
                {to && <ArrowUpRight size={14} strokeWidth={1.75} className="text-[#4B5565]" />}
            </div>
            <p className="mt-3 text-xs text-[#4B5565] leading-relaxed">{desc}</p>
        </>
    );
    if (!to) {
        return (
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] opacity-70" data-testid={testid} data-perm={perm || "any"}>
                {inner}
                <div className="mt-2 text-[11px] text-[#B45309]">Coming in DEV-002.</div>
            </div>
        );
    }
    return (
        <Link to={to} className="rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-[#CBD5E1] hover:shadow-md hover:-translate-y-[1px] transition-all block" data-testid={testid} data-perm={perm || "any"}>
            {inner}
        </Link>
    );
}
